import React, { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CreditCard, ShieldCheck } from 'lucide-react';
import type { LscBillingStatusResponse } from '../types';
import {
  billingCadenceLabel,
  billingLifecycleCustomerCopy,
  billingLifecycleHeadline,
  formatBillingPeriodEnd,
  isCompedEntitlement,
  LSC_SUBSCRIPTION_PRODUCT_NAME,
} from '../billingCopy';
import { createLscBillingPortalSession, LscApiError } from '../api';
import { lscPath } from '../routing';
import LoadingState from './LoadingState';
import ErrorBanner from './ErrorBanner';

interface BillingStatusCardProps {
  status: LscBillingStatusResponse | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** When set, Stripe Customer Portal returns to this league’s settings page. */
  returnLeagueId?: string;
}

export function getBillingStatusHeadline(status: LscBillingStatusResponse | null): string {
  if (!status || !status.billing_model_enabled || status.status === 'disabled') {
    return 'Subscription billing unavailable';
  }

  const lifecycleHeadline = billingLifecycleHeadline(status.billing_lifecycle?.mode);
  if (lifecycleHeadline) {
    return lifecycleHeadline;
  }

  const entitlement = status.entitlement;
  if (!entitlement) {
    const subStatus = (status.subscription_status || status.status || '')
      .toLowerCase()
      .trim();
    if (subStatus === 'past_due') {
      return 'Subscription — payment attention needed';
    }
    if (subStatus === 'canceled' || subStatus === 'cancelled') {
      return 'Subscription canceled';
    }
    if (subStatus === 'suspended') {
      return 'Subscription suspended';
    }
    if (subStatus === 'active' || subStatus === 'trialing') {
      return 'Subscription active';
    }
    return 'Subscription not active';
  }

  if (isCompedEntitlement(entitlement)) {
    return 'Complimentary access active';
  }

  const subStatus = (entitlement.subscription_status || status.subscription_status || '')
    .toLowerCase()
    .trim();

  if (subStatus === 'past_due') {
    return 'Subscription — payment attention needed';
  }
  if (subStatus === 'canceled' || subStatus === 'cancelled') {
    return 'Subscription canceled';
  }
  if (subStatus === 'suspended') {
    return 'Subscription suspended';
  }
  if (subStatus === 'active' || subStatus === 'trialing') {
    return 'Subscription active';
  }

  return 'Subscription status';
}

export function getBillingStatusDetailLines(
  status: LscBillingStatusResponse | null
): string[] {
  if (!status) return [];

  if (!status.billing_model_enabled || status.status === 'disabled') {
    return ['Billing model is not enabled yet.'];
  }

  const lifecycleMessage =
    status.billing_lifecycle?.customer_message ||
    billingLifecycleCustomerCopy(status.billing_lifecycle?.mode);
  const lifecycleMode = (status.billing_lifecycle?.mode || '').toLowerCase();

  const entitlement = status.entitlement;
  if (!entitlement) {
    if (lifecycleMode === 'billing_unbound' || lifecycleMode === 'billing_recovery_available') {
      return [lifecycleMessage];
    }
    const subStatus = (status.subscription_status || status.status || '')
      .toLowerCase()
      .trim();
    if (subStatus === 'active' || subStatus === 'trialing') {
      const lines = [`Plan: ${LSC_SUBSCRIPTION_PRODUCT_NAME}`];
      const cadence = status.billing_cadence;
      if (cadence) {
        lines.push(`Billing cadence: ${billingCadenceLabel(cadence)}`);
      }
      const periodEnd = formatBillingPeriodEnd(status.current_period_end);
      if (periodEnd) {
        lines.push(
          status.cancel_at_period_end
            ? `Access continues through ${periodEnd}`
            : `Current period ends ${periodEnd}`
        );
      }
      const boundLeagues = status.bound_leagues || [];
      if (boundLeagues.length === 1) {
        lines.push(`Bound league: ${boundLeagues[0].name}`);
      } else if (boundLeagues.length > 1) {
        lines.push(
          `Bound leagues: ${boundLeagues.map((league) => league.name).join(', ')}`
        );
      } else {
        lines.push(
          'Open a league’s Settings page to view or bind league access.'
        );
      }
      return lines;
    }
    if (lifecycleMessage && lifecycleMode) {
      return [lifecycleMessage];
    }
    return ['No league subscription linked yet.'];
  }

  const lines: string[] = [];
  lines.push(`Plan: ${LSC_SUBSCRIPTION_PRODUCT_NAME}`);

  if (isCompedEntitlement(entitlement) || lifecycleMode === 'billing_comped') {
    lines.push('Status: Complimentary access');
    return lines;
  }

  const cadence = entitlement.billing_cadence || status.billing_cadence;
  if (cadence) {
    lines.push(`Billing cadence: ${billingCadenceLabel(cadence)}`);
  }

  const periodEnd = formatBillingPeriodEnd(
    entitlement.current_period_end || status.current_period_end
  );
  if (periodEnd) {
    const cancelAtEnd = entitlement.cancel_at_period_end || status.cancel_at_period_end;
    lines.push(
      cancelAtEnd
        ? `Access continues through ${periodEnd}`
        : `Current period ends ${periodEnd}`
    );
  }

  const subStatus = (entitlement.subscription_status || status.subscription_status || '')
    .toLowerCase()
    .trim();
  if (lifecycleMode === 'billing_past_due' || subStatus === 'past_due') {
    lines.push(
      (lifecycleMode && lifecycleMessage) ||
        'Your subscription needs attention. Update billing to keep access active.'
    );
  }
  if (lifecycleMode === 'billing_canceled' || subStatus === 'canceled' || subStatus === 'cancelled') {
    lines.push(
      (lifecycleMode && lifecycleMessage) ||
        'Your subscription is canceled. Resubscribe to restore paid access.'
    );
  }
  if (
    lifecycleMode === 'billing_incomplete' ||
    lifecycleMode === 'billing_unpaid' ||
    lifecycleMode === 'billing_unknown_review_required'
  ) {
    lines.push(
      lifecycleMessage ||
        'League Stream Center access needs attention. Contact support if you need help.'
    );
  } else if (subStatus === 'suspended') {
    lines.push('League Stream Center access is suspended. Contact support for help.');
  }

  return lines;
}

const BillingStatusCard: React.FC<BillingStatusCardProps> = ({
  status,
  loading = false,
  error = null,
  onRetry,
  returnLeagueId,
}) => {
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const handleManageBilling = async () => {
    if (!status?.can_manage_billing) return;
    setPortalLoading(true);
    setPortalError(null);
    try {
      const result = await createLscBillingPortalSession(returnLeagueId);
      if (result.portal_url) {
        window.location.href = result.portal_url;
        return;
      }
      setPortalError('Billing portal is unavailable right now. Please try again later.');
    } catch (err) {
      const message =
        err instanceof LscApiError
          ? err.message
          : 'Billing portal is unavailable right now. Please try again later.';
      setPortalError(message);
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading billing status..." />;
  }

  const headline = getBillingStatusHeadline(status);
  const detailLines = getBillingStatusDetailLines(status);
  const boundLeagues = status?.bound_leagues || [];
  const lifecycleMode = (status?.billing_lifecycle?.mode || '').toLowerCase();
  const showPastDueBanner =
    Boolean(status) &&
    (lifecycleMode === 'billing_past_due' ||
      lifecycleMode === 'billing_unpaid' ||
      (status?.entitlement?.subscription_status || status?.subscription_status || '')
        .toLowerCase()
        .trim() === 'past_due');
  const showCanceledBanner = Boolean(status) && lifecycleMode === 'billing_canceled';
  const showUnboundBanner =
    Boolean(status) &&
    (lifecycleMode === 'billing_unbound' || status?.billing_lifecycle?.recovery_available === true);

  return (
    <div className="card space-y-4" data-testid="lsc-billing-status-card">
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-primary-400 mt-0.5 shrink-0" aria-hidden />
        <div className="space-y-1 min-w-0">
          <h2 className="font-semibold text-white">{headline}</h2>
          {detailLines.map((line) => (
            <p key={line} className="text-sm text-secondary-300">
              {line}
            </p>
          ))}
        </div>
      </div>

      {error && (
        <div className="space-y-2">
          <ErrorBanner message={error} />
          {onRetry && (
            <button type="button" className="btn-secondary" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      )}

      {showPastDueBanner && (
        <div
          className="rounded-lg border border-amber-700/60 bg-amber-950/40 px-4 py-3 text-sm text-amber-100 flex items-start gap-2"
          data-testid="lsc-billing-past-due-banner"
          role="status"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          <p>
            {(lifecycleMode &&
              (status?.billing_lifecycle?.customer_message ||
                billingLifecycleCustomerCopy(status?.billing_lifecycle?.mode))) ||
              'Your subscription payment needs attention. Use manage billing to update your payment method.'}
          </p>
        </div>
      )}

      {showCanceledBanner && (
        <div
          className="rounded-lg border border-secondary-700/60 bg-secondary-900/40 px-4 py-3 text-sm text-secondary-200"
          data-testid="lsc-billing-canceled-banner"
          role="status"
        >
          <p>
            {status?.billing_lifecycle?.customer_message ||
              'Your subscription is canceled. Resubscribe to restore paid access.'}
          </p>
          <Link to={lscPath('/pricing')} className="btn-secondary inline-flex mt-3 text-sm">
            Resubscribe
          </Link>
        </div>
      )}

      {showUnboundBanner && (
        <div
          className="rounded-lg border border-primary-700/50 bg-primary-950/30 px-4 py-3 text-sm text-primary-100"
          data-testid="lsc-billing-unbound-banner"
          role="status"
        >
          <p>
            {status?.billing_lifecycle?.customer_message ||
              billingLifecycleCustomerCopy('billing_unbound')}
          </p>
        </div>
      )}

      {portalError && (
        <p className="text-sm text-red-400" role="alert" data-testid="lsc-billing-portal-error">
          {portalError}
        </p>
      )}

      {boundLeagues.length > 0 && (
        <div className="space-y-2" data-testid="lsc-billing-bound-leagues">
          {boundLeagues.map((league) => (
            <Link
              key={league.league_id}
              to={lscPath(`/leagues/${league.league_id}`)}
              className="btn-secondary inline-flex text-sm"
            >
              View league: {league.name}
            </Link>
          ))}
        </div>
      )}

      {status?.can_manage_billing && (
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-2"
          onClick={() => void handleManageBilling()}
          disabled={portalLoading}
          data-testid="lsc-billing-portal-cta"
        >
          <CreditCard className="h-4 w-4" aria-hidden />
          {portalLoading ? 'Opening billing portal…' : 'Manage billing'}
        </button>
      )}
    </div>
  );
};

export default BillingStatusCard;
